import { useCallback, useRef } from 'react';

import type { LongPressOptions } from '.';

/**
 * React hook for long-press gestures.
 * @param callback - Called on long press.
 * @param options - delay, distance, preventDefault.
 * @returns Pointer event handlers.
 * @example
 * const longPress = useLongPress(cb, { delay: 500 });
 */
export default function useLongPress<T extends EventTarget>(longPressCallback: (event: React.PointerEvent<T>, isTouch: boolean) => void, options: LongPressOptions = {}) {
  const { preventDefault = true, delay = 300, distance = 5 } = options;
  const timerRef = useRef<number | null>(null);
  const posRef = useRef<[number, number]>([0, 0]);

  /**
   * Determines if the pointer event is a touch event.
   * @param {React.PointerEvent<T>} event
   * @returns {boolean}
   */
  const isTouchEvent = useCallback((event: React.PointerEvent<T>) => {
    return event.pointerType === 'touch';
  }, []);

  /**
   * Clears the active timeout referenced by `timerRef.current` and resets it to `null`.
   *
   * This function is used to clean up any pending timer to prevent unintended execution.
   * If there is no active timer, the function returns immediately.
   *
   * @remarks
   * Should be called when the long-press interaction ends or is cancelled to avoid memory leaks.
   */
  const cleanupTimer = useCallback(() => {
    if (!timerRef.current) return;
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  /**
   * Determines whether the default action for a pointer event should be prevented.
   *
   * @param event - The pointer event to evaluate.
   * @returns `true` if `preventDefault` is enabled and the pointer type is not 'touch'; otherwise, `false`.
   */
  const shouldPreventDefault = useCallback(
    (event: React.PointerEvent<T>) => {
      return preventDefault && event.pointerType !== 'touch';
    },
    [preventDefault]
  );

  /**
   * Called when the long press timer completes.
   * Invokes the callback and resets the timer.
   */
  const timesup = useCallback(
    (event: React.PointerEvent<T>) => {
      timerRef.current = null;
      const isTouch: boolean = isTouchEvent(event);
      longPressCallback(event, isTouch);
    },
    [timerRef, isTouchEvent, longPressCallback]
  );

  /**
   * Handler for pointer down event.
   * Starts the long press timer and records the initial pointer position.
   */
  const onPointerDownCb = useCallback(
    (event: React.PointerEvent<T>) => {
      if (shouldPreventDefault(event)) {
        event.preventDefault();
      }
      posRef.current = [event.clientX, event.clientY];
      event.persist();
      timerRef.current = window.setTimeout(() => timesup(event), delay);
    },
    [shouldPreventDefault, timesup, delay]
  );

  /**
   * Handler for pointer up event.
   * Cancels the long press timer if pointer is released early.
   */
  const onPointerUpCb = useCallback(
    (event: React.PointerEvent<T>) => {
      if (shouldPreventDefault(event)) {
        event.preventDefault();
      }
      if (timerRef.current) {
        cleanupTimer();
      }
    },
    [timerRef, shouldPreventDefault]
  );

  /**
   * Handler for pointer move event.
   * Cancels the long press timer if pointer moves too far from initial position.
   */
  const onPointerMoveCb = useCallback(
    (event: React.PointerEvent<T>) => {
      if (shouldPreventDefault(event)) {
        event.preventDefault();
      }
      if (timerRef.current) {
        const clientX: number = event.clientX;
        const clientY: number = event.clientY;
        const d: number = (clientX - posRef.current[0]) ** 2 + (clientY - posRef.current[1]) ** 2;
        if (d > distance) {
          cleanupTimer();
        }
      }
    },
    [preventDefault, timerRef, posRef, distance]
  );

  return {
    onPointerDown: onPointerDownCb,
    onPointerUp: onPointerUpCb,
    onPointerMove: onPointerMoveCb,
  };
}
